import React, { useMemo, useState } from "react";

const GDT_SYMBOLS = [
  {
    id: "straightness",
    symbol: "▬",
    name: "Straightness",
    category: "Form",
    zone: "Two parallel lines, two parallel planes, or a cylindrical zone when applied to an axis.",
    appliesTo: "Surface line element or derived median line",
    description: "Controls how straight a line element or axis must be. No datum is required for this form control.",
    usage: "Use it when the feature can vary in location or orientation, but its line form still needs to be limited.",
  },
  {
    id: "flatness",
    symbol: "▱",
    name: "Flatness",
    category: "Form",
    zone: "Two parallel planes separated by the stated tolerance.",
    appliesTo: "Single surface",
    description: "Controls how flat a surface must be independent of datums.",
    usage: "Use it for sealing faces, mounting pads, or any face where local form matters before assembly.",
  },
  {
    id: "circularity",
    symbol: "○",
    name: "Circularity",
    category: "Form",
    zone: "Two concentric circles at each measured cross-section.",
    appliesTo: "Round cross-section",
    description: "Controls roundness at each cross-section of a cylinder, cone, or sphere.",
    usage: "Use it when a round feature must be round locally but does not need a datum relationship.",
  },
  {
    id: "cylindricity",
    symbol: "⌭",
    name: "Cylindricity",
    category: "Form",
    zone: "Two coaxial cylinders separated by the stated tolerance.",
    appliesTo: "Cylindrical surface",
    description: "Controls roundness, straightness, and taper of the full cylindrical surface without datums.",
    usage: "Use it when the entire cylinder form matters and inspection can support a full surface evaluation.",
  },
  {
    id: "profile-line",
    symbol: "⌒",
    name: "Profile of a Line",
    category: "Profile",
    zone: "A 2D tolerance zone around the true line profile.",
    appliesTo: "2D line element",
    description: "Controls the shape of a line element relative to the true profile.",
    usage: "Use it for cross-section checks of shaped edges, cam forms, and contour slices.",
  },
  {
    id: "profile-surface",
    symbol: "⌓",
    name: "Profile of a Surface",
    category: "Profile",
    zone: "A 3D tolerance zone around the true surface profile.",
    appliesTo: "3D surface",
    description: "Controls the shape, size, orientation, and sometimes location of a surface.",
    usage: "Use it for complex contours and cast, molded, or machined surfaces controlled from datums.",
  },
  {
    id: "angularity",
    symbol: "∠",
    name: "Angularity",
    category: "Orientation",
    zone: "Two parallel planes or a cylindrical zone oriented at the basic angle.",
    appliesTo: "Surface, axis, or center plane",
    description: "Controls an angled relationship to one or more datums.",
    usage: "Use it when the basic angle is not 90 degrees and the feature orientation matters.",
  },
  {
    id: "perpendicularity",
    symbol: "⊥",
    name: "Perpendicularity",
    category: "Orientation",
    zone: "Two parallel planes or a cylindrical zone square to the datum.",
    appliesTo: "Surface, axis, or center plane",
    description: "Controls a 90 degree relationship to a datum.",
    usage: "Use it for faces, holes, pins, and slots that must stand square to a datum feature.",
  },
  {
    id: "parallelism",
    symbol: "∥",
    name: "Parallelism",
    category: "Orientation",
    zone: "Two parallel planes or a cylindrical zone parallel to the datum.",
    appliesTo: "Surface, axis, or center plane",
    description: "Controls parallel relationship to a datum while location is controlled elsewhere.",
    usage: "Use it when a feature can shift but must remain parallel to the datum reference.",
  },
  {
    id: "position",
    symbol: "⌖",
    name: "Position",
    category: "Location",
    zone: "Usually a diametrical or width tolerance zone centered on true position.",
    appliesTo: "Hole, pin, slot, tab, or pattern feature",
    description: "Controls the true location of a feature from basic dimensions and datum references.",
    usage: "Use it for hole and pin patterns where assembly location matters. Position is normally reported as a diametrical error.",
    supportsBonus: true,
    supportsCoordinates: true,
  },
  {
    id: "concentricity",
    symbol: "◎",
    name: "Concentricity",
    category: "Location",
    zone: "A cylindrical zone coaxial with the datum axis.",
    appliesTo: "Median points of opposed elements",
    description: "Controls derived median points relative to a datum axis.",
    usage: "Use carefully. It is difficult to inspect and is often replaced by position or runout in modern drawings.",
  },
  {
    id: "symmetry",
    symbol: "⌯",
    name: "Symmetry",
    category: "Location",
    zone: "Two parallel planes centered on the datum center plane.",
    appliesTo: "Median plane or median points",
    description: "Controls derived median points relative to a datum center plane.",
    usage: "Use carefully. It is often replaced by position or profile when practical inspection is important.",
  },
  {
    id: "circular-runout",
    symbol: "↗",
    name: "Circular Runout",
    category: "Runout",
    zone: "Total indicator variation at each circular section while rotated about the datum axis.",
    appliesTo: "Surface of revolution",
    description: "Controls circular elements as the part rotates about a datum axis.",
    usage: "Use it for local wobble checks on diameters, faces, and cones related to a datum axis.",
  },
  {
    id: "total-runout",
    symbol: "⌰",
    name: "Total Runout",
    category: "Runout",
    zone: "Total indicator variation across the full swept surface while rotated about the datum axis.",
    appliesTo: "Entire surface of revolution",
    description: "Controls the full surface as the part rotates about a datum axis.",
    usage: "Use it when runout, straightness, taper, and surface relationship all matter over the full surface.",
  },
  {
    id: "datum",
    symbol: "A",
    name: "Datum Feature",
    category: "Reference",
    zone: "Establishes a theoretically exact reference plane, axis, or center plane.",
    appliesTo: "Surface, axis, plane, or feature used for setup",
    description: "Identifies the physical feature used to establish the datum reference frame.",
    usage: "Use datum references to define how the part is oriented and located for inspection and manufacturing.",
  },
  {
    id: "basic-dimension",
    symbol: "□",
    name: "Basic Dimension",
    category: "Reference",
    zone: "Theoretically exact value; tolerance comes from the related GD&T control.",
    appliesTo: "Theoretically exact size, angle, or location",
    description: "A boxed theoretically exact dimension. The feature control frame supplies the tolerance.",
    usage: "Use basic dimensions to locate true geometry from the datum reference frame.",
  },
];

const numberOrZero = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const format = (value, unit) =>
  `${Number(value || 0).toFixed(unit === "metric" ? 3 : 4)} ${unit === "metric" ? "mm" : "in"}`;

const datumText = (inputs) =>
  [inputs.datumA, inputs.datumB, inputs.datumC].filter(Boolean).join(" | ") || "No datum reference entered";

const buildDimensionalInterpretation = (symbol, inputs) => {
  const unit = inputs.unit;
  const tolerance = numberOrZero(inputs.tolerance);
  const measuredError = numberOrZero(inputs.measuredError);
  const basicX = numberOrZero(inputs.basicX);
  const basicY = numberOrZero(inputs.basicY);
  const actualX = numberOrZero(inputs.actualX);
  const actualY = numberOrZero(inputs.actualY);
  const actual = numberOrZero(inputs.actualFeatureSize);
  const mmc = numberOrZero(inputs.mmcSize);
  const isHole = inputs.featureType === "hole";
  const supportsBonus = symbol.supportsBonus && inputs.materialCondition === "MMC" && actual > 0 && mmc > 0;
  const bonus = supportsBonus ? Math.max(0, isHole ? actual - mmc : mmc - actual) : 0;
  const totalTolerance = tolerance + bonus;
  const xDeviation = actualX || basicX ? actualX - basicX : 0;
  const yDeviation = actualY || basicY ? actualY - basicY : 0;
  const coordinatePositionError = symbol.supportsCoordinates
    ? 2 * Math.sqrt((xDeviation ** 2) + (yDeviation ** 2))
    : 0;
  const evaluationError = coordinatePositionError || measuredError;
  const hasEvaluation = evaluationError > 0;
  const passFail = totalTolerance > 0 && hasEvaluation
    ? (evaluationError <= totalTolerance ? "Pass" : "Fail")
    : "Enter measured error or coordinates";

  const common = {
    allowedTolerance: totalTolerance,
    bonus,
    datumReference: datumText(inputs),
    xDeviation,
    yDeviation,
    coordinatePositionError,
    passFail,
    headline: tolerance
      ? `Control ${inputs.controlledFeature || "the selected feature"} within ${format(totalTolerance, unit)}.`
      : "Enter the feature control frame tolerance to calculate the requirement.",
    details: [
      `Tolerance zone: ${symbol.zone}`,
      `Datum reference: ${datumText(inputs)}.`,
      `Material condition: ${inputs.materialCondition}. ${inputs.materialCondition === "MMC" ? "Bonus tolerance can apply when the feature departs from MMC." : "No MMC bonus is applied unless MMC is specified."}`,
      `Measured status: ${passFail}.`,
    ],
    inspection: [
      "Establish the datum reference frame in the order shown on the drawing before judging the controlled feature.",
      "Use the drawing's basic dimensions and feature control frame; do not apply title-block plus/minus tolerance to basic dimensions.",
    ],
    formulas: [
      "Total available tolerance = stated GD&T tolerance + applicable bonus tolerance",
      "Pass when measured GD&T error is less than or equal to total available tolerance",
    ],
  };

  if (!tolerance) return common;

  if (symbol.id === "position") {
    return {
      ...common,
      headline: `Achieve a diametrical true-position zone of ${format(totalTolerance, unit)}.`,
      details: [
        `Base position tolerance: ${format(tolerance, unit)}.`,
        `Bonus tolerance: ${format(bonus, unit)}.`,
        `Total allowed position error: ${format(totalTolerance, unit)}.`,
        coordinatePositionError
          ? `Coordinate true-position error from entered X/Y values: ${format(coordinatePositionError, unit)}.`
          : "Enter basic and actual X/Y values to calculate coordinate true-position error.",
        `Measured status: ${passFail}.`,
      ],
      inspection: [
        "Inspect the feature axis or center plane relative to the datum reference frame.",
        "For a round hole or pin, true position is commonly reported as two times the radial coordinate error.",
        "For MMC callouts, verify the actual feature size before adding any bonus tolerance.",
      ],
      formulas: [
        "X deviation = actual X - basic X",
        "Y deviation = actual Y - basic Y",
        "True position error = 2 x sqrt(X deviation^2 + Y deviation^2)",
        "MMC bonus tolerance = actual size - MMC size for holes, or MMC size - actual size for pins",
      ],
    };
  }

  if (["flatness", "straightness", "parallelism", "perpendicularity", "angularity", "profile-line", "profile-surface", "symmetry"].includes(symbol.id)) {
    return {
      ...common,
      headline: `Keep the controlled geometry inside a ${format(totalTolerance, unit)} wide tolerance zone.`,
      details: [
        `Zone type: ${symbol.zone}`,
        `For an equally disposed profile-style zone, this is ${format(totalTolerance / 2, unit)} each side of true geometry unless the drawing says otherwise.`,
        `Measured status: ${passFail}.`,
      ],
    };
  }

  if (["circular-runout", "total-runout"].includes(symbol.id)) {
    return {
      ...common,
      headline: `Keep total indicator variation at or below ${format(totalTolerance, unit)}.`,
      details: [
        `Zone type: ${symbol.zone}`,
        `Measured status: ${passFail}.`,
      ],
      inspection: [
        "Set the part up on the referenced datum axis.",
        "Rotate the part and record the full indicator reading spread on the controlled surface.",
      ],
      formulas: ["Runout error = maximum indicator reading - minimum indicator reading"],
    };
  }

  if (["circularity", "cylindricity"].includes(symbol.id)) {
    return {
      ...common,
      headline: `Keep roundness/form error at or below ${format(totalTolerance, unit)}.`,
      details: [
        `Zone type: ${symbol.zone}`,
        "This form control does not use a datum by itself.",
        `Measured status: ${passFail}.`,
      ],
    };
  }

  return common;
};

const GDTCalculator = ({ onSave }) => {
  const [selectedId, setSelectedId] = useState("position");
  const [query, setQuery] = useState("");
  const [inputs, setInputs] = useState({
    unit: "inch",
    tolerance: 0.005,
    controlledFeature: "hole pattern",
    materialCondition: "RFS",
    featureType: "hole",
    actualFeatureSize: "",
    mmcSize: "",
    basicX: 1,
    basicY: 1,
    actualX: 1.002,
    actualY: 0.998,
    measuredError: "",
    datumA: "A",
    datumB: "B",
    datumC: "C",
  });

  const selected = GDT_SYMBOLS.find((item) => item.id === selectedId) || GDT_SYMBOLS[0];
  const interpretation = buildDimensionalInterpretation(selected, inputs);
  const filteredSymbols = useMemo(() => {
    const clean = query.trim().toLowerCase();
    if (!clean) return GDT_SYMBOLS;
    return GDT_SYMBOLS.filter((item) =>
      [item.name, item.category, item.description, item.symbol, item.usage].join(" ").toLowerCase().includes(clean)
    );
  }, [query]);

  const updateInput = (field, value) => {
    setInputs((current) => ({ ...current, [field]: value }));
  };

  const handleSave = () => {
    onSave?.({
      summary: `${selected.name} ${selected.symbol} - ${interpretation.headline}`,
      input: inputs,
      results: {
        symbol: selected.symbol,
        name: selected.name,
        category: selected.category,
        interpretation,
      },
    });
  };

  const renderInput = (label, field, type = "number", step = "0.0001") => (
    <div className="form-group">
      <label>{label}</label>
      <input
        type={type}
        step={type === "number" ? step : undefined}
        value={inputs[field]}
        onChange={(event) => updateInput(field, event.target.value)}
      />
    </div>
  );

  return (
    <div className="calculator-page">
      <div className="calculator-shell">
        <div className="calculator-header">
          <h1>GD&amp;T Helper</h1>
          <p>
            Browse GD&amp;T symbols, enter drawing and inspection values, and interpret what the callout requires.
          </p>
        </div>

        <div className="calculator-layout calculator-layout-wide-results">
          <div className="calculator-panel">
            <div className="form-group">
              <label htmlFor="gdt-search">Search Symbols</label>
              <input
                id="gdt-search"
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search position, flatness, runout..."
              />
            </div>

            <div style={{ display: "grid", gap: "8px" }}>
              {filteredSymbols.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setSelectedId(item.id)}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "44px 1fr",
                    gap: "12px",
                    alignItems: "center",
                    width: "100%",
                    padding: "12px",
                    borderRadius: "8px",
                    border: `1px solid ${selected.id === item.id ? "var(--accent)" : "var(--border-color)"}`,
                    background: selected.id === item.id ? "var(--accent-soft)" : "var(--bg-panel)",
                    color: "var(--text-primary)",
                    textAlign: "left",
                    cursor: "pointer",
                  }}
                >
                  <span style={{ fontSize: "1.6rem", fontWeight: 800, lineHeight: 1 }}>{item.symbol}</span>
                  <span>
                    <strong style={{ display: "block" }}>{item.name}</strong>
                    <small style={{ color: "var(--text-muted)" }}>{item.category}</small>
                  </span>
                </button>
              ))}
            </div>
          </div>

          <div className="calculator-panel">
            <div style={{ display: "flex", gap: "16px", alignItems: "center", marginBottom: "18px" }}>
              <div style={{ fontSize: "3rem", fontWeight: 900, color: "var(--accent)", lineHeight: 1 }}>
                {selected.symbol}
              </div>
              <div>
                <h2 style={{ margin: 0 }}>{selected.name}</h2>
                <p style={{ margin: 0 }}>{selected.category} control | {selected.appliesTo}</p>
              </div>
            </div>

            <p>{selected.description}</p>
            <p><strong style={{ color: "var(--text-primary)" }}>How it is used:</strong> {selected.usage}</p>
            <p><strong style={{ color: "var(--text-primary)" }}>Tolerance zone:</strong> {selected.zone}</p>

            <div className="calculator-input-grid">
              <div className="form-group">
                <label>Unit</label>
                <select value={inputs.unit} onChange={(event) => updateInput("unit", event.target.value)}>
                  <option value="inch">Imperial</option>
                  <option value="metric">Metric</option>
                </select>
              </div>
              {renderInput("Controlled Feature", "controlledFeature", "text")}
              {renderInput("GD&T Tolerance", "tolerance")}
              <div className="form-group">
                <label>Material Condition</label>
                <select value={inputs.materialCondition} onChange={(event) => updateInput("materialCondition", event.target.value)}>
                  <option value="RFS">RFS</option>
                  <option value="MMC">MMC</option>
                  <option value="LMC">LMC</option>
                </select>
              </div>
              <div className="form-group">
                <label>Feature Type</label>
                <select value={inputs.featureType} onChange={(event) => updateInput("featureType", event.target.value)}>
                  <option value="hole">Hole / Internal Feature</option>
                  <option value="pin">Pin / External Feature</option>
                  <option value="surface">Surface</option>
                </select>
              </div>
              {renderInput("Actual Feature Size", "actualFeatureSize")}
              {renderInput("MMC Size", "mmcSize")}
              {renderInput("Basic X", "basicX")}
              {renderInput("Basic Y", "basicY")}
              {renderInput("Actual X", "actualX")}
              {renderInput("Actual Y", "actualY")}
              {renderInput("Measured GD&T Error / TIR", "measuredError")}
              {renderInput("Primary Datum", "datumA", "text")}
              {renderInput("Secondary Datum", "datumB", "text")}
              {renderInput("Tertiary Datum", "datumC", "text")}
            </div>

            <div className="calculator-result-card" style={{ marginTop: "18px" }}>
              <span className="calculator-result-label">Dimension Requirement</span>
              <strong className="calculator-result-value">{interpretation.headline}</strong>
              <ul style={{ margin: "12px 0 0", paddingLeft: "18px", color: "var(--text-secondary)" }}>
                {interpretation.details.map((detail) => (
                  <li key={detail}>{detail}</li>
                ))}
              </ul>
            </div>

            <div className="calculator-results-grid" style={{ marginTop: "18px" }}>
              <div className="calculator-result-card">
                <span className="calculator-result-label">Allowed Tolerance</span>
                <strong className="calculator-result-value">{format(interpretation.allowedTolerance, inputs.unit)}</strong>
              </div>
              <div className="calculator-result-card">
                <span className="calculator-result-label">Bonus Tolerance</span>
                <strong className="calculator-result-value">{format(interpretation.bonus, inputs.unit)}</strong>
              </div>
              <div className="calculator-result-card">
                <span className="calculator-result-label">Coordinate Position Error</span>
                <strong className="calculator-result-value">{format(interpretation.coordinatePositionError, inputs.unit)}</strong>
              </div>
              <div className="calculator-result-card">
                <span className="calculator-result-label">Inspection Result</span>
                <strong className="calculator-result-value">{interpretation.passFail}</strong>
              </div>
            </div>

            <section style={{ marginTop: "18px" }}>
              <h4>Inspection Notes</h4>
              <ul style={{ color: "var(--text-secondary)", paddingLeft: "18px" }}>
                {interpretation.inspection.map((note) => <li key={note}>{note}</li>)}
              </ul>
            </section>

            <section style={{ marginTop: "18px" }}>
              <h4>Formula Used</h4>
              <ul style={{ color: "var(--text-secondary)", paddingLeft: "18px" }}>
                {interpretation.formulas.map((formula) => <li key={formula}>{formula}</li>)}
              </ul>
            </section>

            <div className="calculator-actions">
              <button type="button" className="btn btn-primary" onClick={handleSave}>
                Save GD&amp;T Note
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GDTCalculator;
